import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Swiper, SwiperSlide } from 'swiper/react';
import { motion } from "framer-motion";
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import { Pagination, Autoplay, Navigation } from 'swiper/modules';
import ContactSection from "../../components/ContactSection";

const EnterpriseNetworking = () => {

    const [activeSlide, setActiveSlide] = useState(0);
    const [openFaq, setOpenFaq] = useState(null);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const features = [
        {
            id: "01",
            title: "Passive Cabling",
            description: "We design and deliver end-to-end cabling systems that form the physical backbone of enterprise networks - from structured cabling, trunking, patch panels and labelling to full infrastructure documentation and compliance."
        },
        {
            id: "02",
            title: "Wireless Infrastructure",
            description: "Our wireless solutions deliver secure, high-density connectivity for offices, campuses, warehouses and outdoor environments. We enable seamless mobility, real-world coverage optimisation and enterprise-grade management."
        },
        {
            id: "03",
            title: "Switching Infrastructure",
            description: "We deploy high-performance access switching, reliable distribution layer and resilient core switching solutions for enterprise-grade availability and high-throughput performance."
        },
        {
            id: "04",
            title: "Industrial Networking",
            description: "Ruggedised switches, wireless APs and routers designed for harsh OT/IoT environments including manufacturing floors, utilities infrastructure and smart building systems."
        },
        {
            id: "05",
            title: "SD-WAN & Multi-Site",
            description: "Intelligent software-defined WAN solutions connecting multiple sites with centralised policy management, application-aware routing and integrated security posture."
        },
        {
            id: "06",
            title: "Network Security",
            description: "End-to-end network security from perimeter firewalls and next-gen intrusion prevention to micro-segmentation, Zero Trust access and SASE-ready architectures."
        }
    ];

    const managedServices = [
        "Network architecture design and optimisation",
        "Switching and wireless performance tuning",
        "Secure connectivity and access controls",
        "SD-WAN and multi-site networking",
        "Monitoring and fault management",
        "Compliance and security integration",
        "Backup connectivity and resilience planning"
    ];

    const caseStudies = [
        {
            tag: "Networking Services",
            title: "Network Modernisation And Infrastructure Transformation Programs",
            image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&auto=format&fit=crop&q=80",
            dark: true
        },
        {
            tag: "Networking Services",
            title: "Explore how we transformed enterprise modernisation",
            image: "https://images.unsplash.com/photo-1573164713988-8665fc963095?w=600&auto=format&fit=crop&q=80",
            dark: false
        },
        {
            tag: "Networking Services",
            title: "SD-WAN Deployment Across 50+ Global Branch Locations",
            image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&auto=format&fit=crop&q=80",
            dark: true
        },
        {
            tag: "Networking Services",
            title: "Secure Wireless Campus Rollout for 10,000+ Users",
            image: "https://images.unsplash.com/photo-1504384308090-c894fdcc538d?w=600&auto=format&fit=crop&q=80",
            dark: false
        }
    ];

    const faqs = [
        {
            question: "What types of enterprise networking services do you provide?",
            answer: "We provide end-to-end enterprise networking solutions including structured cabling (passive & active), wireless infrastructure (Wi-Fi 6/6E), LAN switching, WAN optimization, SD-WAN deployments, industrial networking, and network security. Our services cover design, procurement, installation, configuration, and ongoing managed support."
        },
        {
            question: "Can you support multi-site or campus-wide deployments?",
            answer: "Yes. We specialize in multi-site and campus-wide networking projects. Whether it's a single office refresh or a national SD-WAN rollout across 100+ sites, our certified network engineers have the project management capabilities and vendor relationships to deliver at scale."
        },
        {
            question: "Do you support both on-premise and cloud networking models?",
            answer: "Absolutely. We design hybrid networking architectures that bridge on-premise infrastructure with cloud connectivity. This includes direct cloud on-ramps (AWS, Azure, GCP), SASE frameworks, and SD-WAN solutions that provide application-aware routing between on-prem and cloud workloads."
        },
        {
            question: "What network monitoring and management services do you offer?",
            answer: "Our Network Operations Centre (NOC) provides 24/7 proactive monitoring, performance management, fault detection, and incident resolution. We use industry-leading tools like Cisco DNA Center, Aruba Central, and SolarWinds to ensure your network is always performing at its best."
        },
        {
            question: "Which networking vendors do you work with?",
            answer: "We are vendor-agnostic but maintain elite partnerships with Cisco, Aruba (HPE), Juniper, Fortinet, Palo Alto Networks, Extreme Networks, and Ruckus. This allows us to recommend the best-fit solution for your specific requirements rather than being tied to a single ecosystem."
        },
        {
            question: "How do you ensure network security is built in?",
            answer: "Security is embedded at every layer of our networking designs. We apply Zero Trust principles, enforce network access control (NAC), implement micro-segmentation, and integrate with SIEM/SOC platforms. All wireless deployments include WPA3 encryption, rogue AP detection, and guest network isolation as standard."
        }
    ];

    return (
        <>
            {/* ── HERO BANNER ── */}
            <section style={{
                position: 'relative',
                width: '100%',
                minHeight: '480px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundImage: `url('https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=1600&auto=format&fit=crop&q=80')`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                overflow: 'hidden'
            }}>
                <div style={{
                    position: 'absolute', inset: 0,
                    background: 'rgba(10,20,50,0.72)'
                }} />
                <div className="container" style={{ position: 'relative', zIndex: 2, padding: '120px 15px 80px', textAlign: 'center' }}>
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                    >
                        <h1 style={{
                            color: '#fff', fontWeight: 600,
                            fontSize: 'clamp(2rem, 4.5vw, 3rem)',
                            lineHeight: 1.2, marginBottom: '20px',
                            maxWidth: '920px', margin: '0 auto 20px'
                        }}>
                            Enterprise Networking Solutions for a Connected, Scalable Future
                        </h1>
                        <p style={{
                            color: 'rgba(255,255,255,0.78)', fontSize: '1rem',
                            lineHeight: 1.75, maxWidth: '620px', margin: '0 auto 36px'
                        }}>
                            Build a resilient, high-performance network infrastructure that powers seamless connectivity, operational efficiency, and business growth
                        </p>
                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <a href="#services-overview" style={{ textDecoration: 'none' }}>
                                <button style={{
                                    display: 'inline-flex', alignItems: 'center', gap: '0',
                                    background: 'linear-gradient(135deg,#2563eb,#0ea5e9)',
                                    color: '#fff', border: 'none',
                                    padding: '14px 8px 14px 28px',
                                    borderRadius: '50px', fontWeight: 700, fontSize: '1rem',
                                    cursor: 'pointer', transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                                    boxShadow: '0 8px 24px rgba(37,99,235,0.4)'
                                }}
                                    onMouseOver={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 12px 32px rgba(37,99,235,0.55)'; }}
                                    onMouseOut={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(37,99,235,0.4)'; }}
                                >
                                    <span style={{ marginRight: '16px' }}>Know More</span>
                                    <span style={{
                                        background: '#fff', borderRadius: '50%',
                                        width: '38px', height: '38px',
                                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                        flexShrink: 0
                                    }}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                            <path d="M7 17L17 7M17 7H7M17 7V17" stroke="#0f172a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </span>
                                </button>
                            </a>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* ── WHAT IS ENTERPRISE NETWORKING ── */}
            <section id="services-overview" style={{ padding: '90px 0', background: '#fff' }}>
                <div className="container">
                    <div className="row align-items-center g-5">
                        {/* Left: text */}
                        <div className="col-lg-6">
                            <p style={{
                                color: '#2563eb', fontWeight: 700, letterSpacing: '3px', textAlign: 'left',
                                textTransform: 'uppercase', fontSize: '0.78rem', marginBottom: '16px'
                            }}>
                                ENTERPRISE NETWORKING, EXPLAINED
                            </p>
                            <h2 style={{
                                fontSize: 'clamp(1.9rem, 3vw, 2.5rem)', fontWeight: 600, textAlign: 'left',
                                color: '#0f172a', lineHeight: 1.25, marginBottom: '28px'
                            }}>
                                What is Enterprise Networking?
                            </h2>
                            <p style={{ color: '#475569', fontSize: '1.05rem', lineHeight: 1.85, marginBottom: '20px', textAlign: 'left' }}>
                                Enterprise networking services enable organisations to design, deploy, and manage secure, high-performance network infrastructure. We integrate structured cabling, switching, wireless, routing, SD-WAN, and network management into unified ecosystems that support modern business commerce.
                            </p>
                            <p style={{ color: '#475569', fontSize: '1.05rem', lineHeight: 1.85, marginBottom: '32px', textAlign: 'left' }}>
                                Our approach ensures <strong style={{ color: '#0f172a' }}>visibility, reliability, and scalability</strong> while reducing operational risk and complexity.
                            </p>
                            <Link to="/contact" style={{
                                display: 'inline-flex', alignItems: 'center', gap: '10px',
                                background: 'linear-gradient(135deg,#2563eb,#0ea5e9)', color: '#fff',
                                padding: '13px 28px', borderRadius: '8px', fontWeight: 700,
                                fontSize: '0.9rem', textDecoration: 'none',
                                boxShadow: '0 6px 20px rgba(37,99,235,0.3)',
                                transition: 'transform 0.2s, box-shadow 0.2s'
                            }}
                                onMouseOver={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 10px 28px rgba(37,99,235,0.45)'; }}
                                onMouseOut={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(37,99,235,0.3)'; }}
                            >
                                Discuss Your Network Project
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M7 17L17 7M17 7H7M17 7V17" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                            </Link>
                        </div>

                        {/* Right: image with floating stats */}
                        <div className="col-lg-6">
                            <div style={{ position: 'relative' }}>
                                <img
                                    src="https://images.unsplash.com/photo-1573164713988-8665fc963095?w=800&auto=format&fit=crop&q=80"
                                    alt="Enterprise Networking Team"
                                    style={{ width: '100%', borderRadius: '16px', objectFit: 'cover', height: '400px', display: 'block' }}
                                />
                                {/* Stat overlay */}

                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── NETWORKING BUILT FOR PERFORMANCE (Features Grid) ── */}
            <section style={{ padding: '110px 0', background: '#0b1120', position: 'relative', overflow: 'hidden' }}>
                {/* Subtle grid background */}
                <div style={{
                    position: 'absolute', inset: 0,
                    backgroundImage: 'linear-gradient(rgba(37,99,235,0.07) 1px, transparent 1px), linear-gradient(90deg, rgba(37,99,235,0.07) 1px, transparent 1px)',
                    backgroundSize: '60px 60px',
                    pointerEvents: 'none'
                }} />
                {/* Glow blobs */}
                <div style={{
                    position: 'absolute', top: '-120px', left: '-120px',
                    width: '500px', height: '500px', borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(37,99,235,0.18) 0%, transparent 70%)',
                    pointerEvents: 'none'
                }} />
                <div style={{
                    position: 'absolute', bottom: '-100px', right: '-100px',
                    width: '440px', height: '440px', borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(14,165,233,0.14) 0%, transparent 70%)',
                    pointerEvents: 'none'
                }} />

                <div className="container" style={{ position: 'relative', zIndex: 2 }}>
                    {/* Section header */}
                    <div style={{ textAlign: 'center', marginBottom: '72px' }}>
                        <div style={{
                            display: 'inline-flex', alignItems: 'center', gap: '8px',
                            background: 'rgba(37,99,235,0.15)', border: '1px solid rgba(37,99,235,0.3)',
                            borderRadius: '50px', padding: '6px 18px', marginBottom: '24px'
                        }}>
                            <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#60a5fa' }} />
                            <span style={{ color: '#60a5fa', fontWeight: 700, letterSpacing: '2.5px', textTransform: 'uppercase', fontSize: '0.72rem' }}>
                                Our Solutions
                            </span>
                        </div>
                        <h2 style={{
                            fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: 800,
                            color: '#fff', lineHeight: 1.2, marginBottom: '20px'
                        }}>
                            Networking Built for{' '}
                            <span style={{
                                background: 'linear-gradient(135deg, #60a5fa, #38bdf8)',
                                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent'
                            }}>
                                Performance
                            </span>
                            {' '}and Control
                        </h2>
                        <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '1.05rem', maxWidth: '560px', margin: '0 auto', lineHeight: 1.75 }}>
                            End-to-end networking expertise across every layer of your infrastructure - from physical cabling to intelligent SD-WAN.
                        </p>
                    </div>

                    {/* Features grid */}
                    <div className="row g-4">
                        {[
                            {
                                id: '01', title: 'Passive Cabling',
                                icon: (
                                    <svg width="26" height="26" viewBox="0 0 24 24" fill="none"><path d="M4 6h16M4 12h16M4 18h7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /><circle cx="18" cy="18" r="3" stroke="currentColor" strokeWidth="2" /></svg>
                                ),
                                description: 'We design and deliver end-to-end cabling systems that form the physical backbone of enterprise networks - from structured cabling, trunking, patch panels and labelling to full infrastructure documentation and compliance.',
                                accent: '#2563eb'
                            },
                            {
                                id: '02', title: 'Wireless Infrastructure',
                                icon: (
                                    <svg width="26" height="26" viewBox="0 0 24 24" fill="none"><path d="M5 12.55a11 11 0 0 1 14.08 0M1.42 9a16 16 0 0 1 21.16 0M8.53 16.11a6 6 0 0 1 6.95 0" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /><circle cx="12" cy="20" r="1" fill="currentColor" /></svg>
                                ),
                                description: 'Our wireless solutions deliver secure, high-density connectivity for offices, campuses, warehouses and outdoor environments. We enable seamless mobility, real-world coverage optimisation and enterprise-grade management.',
                                accent: '#0ea5e9'
                            },
                            {
                                id: '03', title: 'Switching Infrastructure',
                                icon: (
                                    <svg width="26" height="26" viewBox="0 0 24 24" fill="none"><rect x="2" y="8" width="20" height="8" rx="2" stroke="currentColor" strokeWidth="2" /><path d="M6 12h.01M10 12h.01M14 12h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /><path d="M18 8V6M18 16v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
                                ),
                                description: 'We deploy high-performance access switching, reliable distribution layer and resilient core switching solutions for enterprise-grade availability and high-throughput performance.',
                                accent: '#6366f1'
                            },
                            {
                                id: '04', title: 'Industrial Networking',
                                icon: (
                                    <svg width="26" height="26" viewBox="0 0 24 24" fill="none"><path d="M12 2L2 7l10 5 10-5-10-5z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" /><path d="M2 17l10 5 10-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /><path d="M2 12l10 5 10-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                ),
                                description: 'Ruggedised switches, wireless APs and routers designed for harsh OT/IoT environments including manufacturing floors, utilities infrastructure and smart building systems.',
                                accent: '#f59e0b'
                            },
                            {
                                id: '05', title: 'SD-WAN & Multi-Site',
                                icon: (
                                    <svg width="26" height="26" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" /><path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" stroke="currentColor" strokeWidth="2" /></svg>
                                ),
                                description: 'Intelligent software-defined WAN solutions connecting multiple sites with centralised policy management, application-aware routing and integrated security posture.',
                                accent: '#10b981'
                            },
                            {
                                id: '06', title: 'Network Security',
                                icon: (
                                    <svg width="26" height="26" viewBox="0 0 24 24" fill="none"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" /><path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                ),
                                description: 'End-to-end network security from perimeter firewalls and next-gen intrusion prevention to micro-segmentation, Zero Trust access and SASE-ready architectures.',
                                accent: '#ef4444'
                            }
                        ].map((f, i) => (
                            <div key={f.id} className="col-lg-4 col-md-6">
                                <motion.div
                                    initial={{ opacity: 0, y: 24 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5, delay: i * 0.08 }}
                                    viewport={{ once: true }}
                                    whileHover={{ y: -6 }}
                                    style={{
                                        background: 'rgba(255,255,255,0.04)',
                                        border: '1px solid rgba(255,255,255,0.09)',
                                        borderRadius: '16px',
                                        padding: '36px 30px',
                                        height: '100%',
                                        cursor: 'default',
                                        transition: 'border-color 0.3s, background 0.3s',
                                        position: 'relative',
                                        overflow: 'hidden'
                                    }}
                                    onMouseOver={e => { e.currentTarget.style.borderColor = f.accent + '55'; e.currentTarget.style.background = 'rgba(255,255,255,0.07)'; }}
                                    onMouseOut={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.09)'; e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
                                >
                                    {/* Top accent line */}
                                    <div style={{
                                        position: 'absolute', top: 0, left: '30px', right: '30px',
                                        height: '2px', borderRadius: '0 0 4px 4px',
                                        background: `linear-gradient(90deg, ${f.accent}, transparent)`
                                    }} />

                                    {/* Icon */}
                                    <div style={{
                                        width: '52px', height: '52px', borderRadius: '12px',
                                        background: f.accent + '20',
                                        border: `1px solid ${f.accent}40`,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        color: f.accent, marginBottom: '22px'
                                    }}>
                                        {f.icon}
                                    </div>

                                    {/* Number */}
                                    <div style={{
                                        fontSize: '0.72rem', fontWeight: 700, color: 'rgba(255,255,255,0.25)',
                                        letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '10px'
                                    }}>
                                        {f.id}
                                    </div>

                                    <h3 style={{
                                        fontSize: '1.1rem', fontWeight: 700, color: '#f1f5f9',
                                        marginBottom: '14px', lineHeight: 1.4
                                    }}>{f.title}</h3>

                                    <p style={{
                                        color: 'rgba(255,255,255,0.48)', fontSize: '0.9rem',
                                        lineHeight: 1.8, marginBottom: 0
                                    }}>{f.description}</p>
                                </motion.div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── FULL-LIFECYCLE NETWORK MANAGEMENT (Dark Section) ── */}
            <section style={{
                position: 'relative', overflow: 'hidden',
                background: '#0f172a', padding: '100px 0'
            }}>
                {/* Background image with overlay */}
                <div style={{
                    position: 'absolute', inset: 0,
                    backgroundImage: `url('https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=1600&auto=format&fit=crop&q=80')`,
                    backgroundSize: 'cover', backgroundPosition: 'center',
                    opacity: 0.18
                }} />
                <div className="container" style={{ position: 'relative', zIndex: 2 }}>
                    <div className="row align-items-center g-5">
                        <div className="col-lg-5">
                            <p style={{
                                color: '#38bdf8', fontWeight: 700, letterSpacing: '3px',
                                textTransform: 'uppercase', fontSize: '0.78rem', marginBottom: '16px'
                            }}>
                                MANAGED SERVICES SPECTRUM
                            </p>
                            <h2 style={{
                                color: '#fff', fontWeight: 800,
                                fontSize: 'clamp(1.9rem, 3.5vw, 2.8rem)',
                                lineHeight: 1.25, marginBottom: '20px'
                            }}>
                                Full-Lifecycle Network Management
                            </h2>
                            <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: '1.05rem', lineHeight: 1.85 }}>
                                Comprehensively spanning from strategy to operations - we own every stage of your network lifecycle.
                            </p>
                        </div>
                        <div className="col-lg-7">
                            <div className="row g-3">
                                {managedServices.map((svc, i) => (
                                    <div key={i} className="col-md-6">
                                        <motion.div
                                            initial={{ opacity: 0, x: 20 }}
                                            whileInView={{ opacity: 1, x: 0 }}
                                            transition={{ duration: 0.4, delay: i * 0.08 }}
                                            viewport={{ once: true }}
                                            style={{
                                                display: 'flex', alignItems: 'center', gap: '14px',
                                                padding: '16px 20px', borderRadius: '10px',
                                                background: 'rgba(255,255,255,0.06)',
                                                border: '1px solid rgba(255,255,255,0.1)',
                                                backdropFilter: 'blur(10px)',
                                                transition: 'background 0.2s'
                                            }}
                                            onMouseOver={e => e.currentTarget.style.background = 'rgba(56,189,248,0.1)'}
                                            onMouseOut={e => e.currentTarget.style.background = 'rgba(255,255,255,0.06)'}
                                        >
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                                                <path d="M5 13L9 17L19 7" stroke="#38bdf8" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                            <span style={{ color: 'rgba(255,255,255,0.88)', fontSize: '0.95rem', fontWeight: 500 }}>{svc}</span>
                                        </motion.div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── WHY CHOOSE SOLARIS (3-column stats) ── */}
            <section style={{ padding: '90px 0', background: '#fff' }}>
                <div className="container">
                    <div style={{ textAlign: 'center', marginBottom: '60px' }}>
                        <p style={{
                            color: '#2563eb', fontWeight: 700, letterSpacing: '3px',
                            textTransform: 'uppercase', fontSize: '0.78rem', marginBottom: '14px'
                        }}>
                            WHY SOLARIS
                        </p>
                        <h2 style={{
                            fontSize: 'clamp(1.9rem, 3.5vw, 2.8rem)', fontWeight: 800,
                            color: '#0f172a', marginBottom: '18px'
                        }}>
                            Trusted Across Industries
                        </h2>
                        <p style={{ color: '#64748b', fontSize: '1.05rem', maxWidth: '580px', margin: '0 auto' }}>
                            Delivering mission-critical network infrastructure with precision, expertise and care.
                        </p>
                    </div>

                    <div className="row g-4">
                        {[
                            { icon: '🏗️', title: 'Design-Led Approach', desc: 'Every network starts with a thorough assessment and custom architecture blueprint - not a one-size-fits-all template.' },
                            { icon: '🔒', title: 'Security by Default', desc: 'Security is never an afterthought. Zero Trust principles, NAC, and encryption are embedded from day one.' },
                            { icon: '📡', title: 'Vendor-Agnostic Expertise', desc: 'Elite partnerships with Cisco, Aruba, Juniper, Fortinet and more - we recommend what works best for you.' },
                            { icon: '🛠️', title: '24/7 NOC Support', desc: 'Round-the-clock monitoring, fault management, and rapid incident response keeping your network always-on.' },
                            { icon: '⚡', title: 'Fast Deployment', desc: 'Proven project methodologies ensuring on-time, on-budget delivery from cabling to SD-WAN rollouts.' },
                            { icon: '📊', title: 'Data-Driven Optimisation', desc: 'Continuous performance analytics and AI-driven insights to keep your network tuned for peak performance.' }
                        ].map((item, i) => (
                            <div key={i} className="col-lg-4 col-md-6">
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5, delay: i * 0.1 }}
                                    viewport={{ once: true }}
                                    style={{
                                        padding: '32px 28px', borderRadius: '16px',
                                        border: '1px solid #e2e8f0',
                                        background: '#fff',
                                        transition: 'border-color 0.2s, box-shadow 0.2s, transform 0.2s',
                                        height: '100%', cursor: 'default'
                                    }}
                                    whileHover={{
                                        y: -6, boxShadow: '0 16px 40px rgba(37,99,235,0.1)',
                                        borderColor: '#93c5fd'
                                    }}
                                >
                                    <div style={{
                                        width: '56px', height: '56px', borderRadius: '14px',
                                        background: 'linear-gradient(135deg,#eff6ff,#dbeafe)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        fontSize: '1.6rem', marginBottom: '20px'
                                    }}>{item.icon}</div>
                                    <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#0f172a', marginBottom: '12px' }}>{item.title}</h3>
                                    <p style={{ color: '#64748b', fontSize: '0.95rem', lineHeight: 1.75, marginBottom: 0 }}>{item.desc}</p>
                                </motion.div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── CASE STUDIES CAROUSEL ── */}
            <section style={{ padding: '90px 0', background: '#f8fafc' }}>
                <div className="container">
                    <div style={{ textAlign: 'center', marginBottom: '60px' }}>
                        <p style={{
                            color: '#2563eb', fontWeight: 700, letterSpacing: '3px',
                            textTransform: 'uppercase', fontSize: '0.78rem', marginBottom: '14px'
                        }}>
                            CASE STUDIES
                        </p>
                        <h2 style={{
                            fontSize: 'clamp(1.9rem, 3.5vw, 2.8rem)', fontWeight: 800,
                            color: '#0f172a', marginBottom: '0'
                        }}>
                            Delivering Reliable<br />
                            Enterprise Connectivity
                        </h2>
                    </div>

                    <div style={{ position: 'relative' }}>
                        <Swiper
                            modules={[Navigation, Autoplay, Pagination]}
                            spaceBetween={24}
                            slidesPerView={1}
                            navigation={{ prevEl: '.cs-prev-btn', nextEl: '.cs-next-btn' }}
                            pagination={{ clickable: true, el: '.cs-pagination' }}
                            autoplay={{ delay: 4000, disableOnInteraction: false }}
                            breakpoints={{ 768: { slidesPerView: 2 }, 1200: { slidesPerView: 3 } }}
                            className="cs-swiper pb-5"
                        >
                            {caseStudies.map((cs, i) => (
                                <SwiperSlide key={i}>
                                    <motion.div
                                        style={{
                                            borderRadius: '14px', overflow: 'hidden',
                                            background: '#fff', border: '1px solid #e2e8f0',
                                            boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
                                            transition: 'transform 0.3s, box-shadow 0.3s'
                                        }}
                                        whileHover={{ y: -6, boxShadow: '0 20px 50px rgba(0,0,0,0.12)' }}
                                    >
                                        <div style={{ position: 'relative', height: '220px', overflow: 'hidden' }}>
                                            <img
                                                src={cs.image}
                                                alt={cs.title}
                                                style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.6s' }}
                                            />
                                            <div style={{
                                                position: 'absolute', inset: 0,
                                                background: 'linear-gradient(to bottom, transparent 40%, rgba(0,0,0,0.6) 100%)'
                                            }} />
                                            {cs.dark && (
                                                <div style={{
                                                    position: 'absolute', bottom: '16px', left: '16px', right: '16px',
                                                    color: '#fff', fontWeight: 700, fontSize: '1rem', lineHeight: 1.4
                                                }}>
                                                    Discover how we transformed video collaboration at scale
                                                </div>
                                            )}
                                        </div>
                                        <div style={{ padding: '24px' }}>
                                            <span style={{
                                                fontSize: '0.75rem', fontWeight: 700, color: '#2563eb',
                                                textTransform: 'uppercase', letterSpacing: '1px',
                                                display: 'block', marginBottom: '10px'
                                            }}>+ {cs.tag}</span>
                                            <h4 style={{
                                                fontSize: '1rem', fontWeight: 700, color: '#0f172a',
                                                lineHeight: 1.5, marginBottom: '20px'
                                            }}>{cs.title}</h4>
                                            <Link to="/case-study" style={{
                                                display: 'inline-flex', alignItems: 'center', gap: '8px',
                                                background: '#fff', color: '#2563eb',
                                                border: '1.5px solid #2563eb', padding: '9px 20px',
                                                borderRadius: '8px', fontSize: '0.85rem', fontWeight: 700,
                                                textDecoration: 'none', transition: 'background 0.2s, color 0.2s'
                                            }}
                                                onMouseOver={e => { e.currentTarget.style.background = '#2563eb'; e.currentTarget.style.color = '#fff'; }}
                                                onMouseOut={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.color = '#2563eb'; }}
                                            >
                                                Read More
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M7 17L17 7M17 7H7M17 7V17" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                            </Link>
                                        </div>
                                    </motion.div>
                                </SwiperSlide>
                            ))}
                        </Swiper>

                        {/* Nav controls */}
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '14px', marginTop: '20px' }}>
                            <button className="cs-prev-btn" style={{
                                width: '40px', height: '40px', borderRadius: '50%',
                                border: '1.5px solid #cbd5e1', background: '#fff', cursor: 'pointer',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                color: '#475569', transition: 'all 0.2s'
                            }}
                                onMouseOver={e => { e.currentTarget.style.background = '#2563eb'; e.currentTarget.style.borderColor = '#2563eb'; e.currentTarget.style.color = '#fff'; }}
                                onMouseOut={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = '#cbd5e1'; e.currentTarget.style.color = '#475569'; }}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M15 19L8 12L15 5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                            </button>
                            <div className="cs-pagination" style={{ display: 'flex', gap: '6px' }} />
                            <button className="cs-next-btn" style={{
                                width: '40px', height: '40px', borderRadius: '50%',
                                border: '1.5px solid #2563eb', background: '#2563eb', cursor: 'pointer',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                color: '#fff', transition: 'all 0.2s'
                            }}
                                onMouseOver={e => { e.currentTarget.style.background = '#1d4ed8'; e.currentTarget.style.borderColor = '#1d4ed8'; }}
                                onMouseOut={e => { e.currentTarget.style.background = '#2563eb'; e.currentTarget.style.borderColor = '#2563eb'; }}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M9 5L16 12L9 19" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                            </button>
                        </div>
                    </div>
                </div>

                <style>{`
                    .cs-swiper .swiper-pagination-bullet {
                        width: 8px; height: 8px;
                        background: #cbd5e1; opacity: 1;
                        border-radius: 50%; margin: 0 4px;
                        transition: all 0.3s;
                    }
                    .cs-swiper .swiper-pagination-bullet-active {
                        background: #2563eb; width: 24px; border-radius: 4px;
                    }
                `}</style>
            </section>

            {/* ── FAQ SECTION ── */}
            <section style={{ padding: '100px 0', background: '#0a0f1e', position: 'relative', overflow: 'hidden' }}>
                {/* Subtle bg texture */}
                <div style={{
                    position: 'absolute', inset: 0,
                    backgroundImage: 'radial-gradient(ellipse at 20% 50%, rgba(37,99,235,0.08) 0%, transparent 60%), radial-gradient(ellipse at 80% 20%, rgba(99,102,241,0.08) 0%, transparent 60%)',
                    pointerEvents: 'none'
                }} />

                <div className="container" style={{ position: 'relative', zIndex: 2 }}>
                    {/* Header */}
                    <div style={{ textAlign: 'center', marginBottom: '64px' }}>
                        <h2 style={{
                            fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: 800,
                            color: '#fff', lineHeight: 1.2, marginBottom: '16px'
                        }}>
                            Frequently asked questions
                        </h2>
                        <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '1rem' }}>
                            Everything you need to know about our services and solutions
                        </p>
                    </div>

                    {/* Two-column layout */}
                    <div className="row g-5 align-items-start">
                        {/* Left: Accordion */}
                        <div className="col-lg-7">
                            {faqs.map((faq, i) => (
                                <motion.div
                                    key={i}
                                    initial={{ opacity: 0, x: -20 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    transition={{ duration: 0.4, delay: i * 0.07 }}
                                    viewport={{ once: true }}
                                    style={{ marginBottom: '12px' }}
                                >
                                    <button
                                        onClick={() => setOpenFaq(openFaq === i ? null : i)}
                                        style={{
                                            width: '100%', textAlign: 'left',
                                            background: openFaq === i ? 'rgba(99,102,241,0.12)' : 'rgba(255,255,255,0.04)',
                                            border: openFaq === i ? '1px solid rgba(99,102,241,0.4)' : '1px solid rgba(255,255,255,0.08)',
                                            borderRadius: openFaq === i ? '12px 12px 0 0' : '12px',
                                            padding: '20px 24px',
                                            display: 'flex', alignItems: 'center', gap: '16px',
                                            cursor: 'pointer', transition: 'all 0.25s'
                                        }}
                                    >
                                        {/* Number badge */}
                                        <span style={{
                                            width: '32px', height: '32px', borderRadius: '50%', flexShrink: 0,
                                            background: openFaq === i ? 'linear-gradient(135deg,#6366f1,#4f46e5)' : 'rgba(255,255,255,0.08)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            fontSize: '0.78rem', fontWeight: 700,
                                            color: openFaq === i ? '#fff' : 'rgba(255,255,255,0.5)',
                                            transition: 'all 0.25s', flexShrink: 0
                                        }}>
                                            {i + 1}
                                        </span>
                                        <span style={{
                                            flex: 1, fontSize: '0.98rem', fontWeight: 600,
                                            color: openFaq === i ? '#fff' : 'rgba(255,255,255,0.75)',
                                            lineHeight: 1.45, textAlign: 'left'
                                        }}>
                                            {faq.question}
                                        </span>
                                        {/* Plus / minus icon */}
                                        <span style={{
                                            width: '24px', height: '24px', flexShrink: 0,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            color: openFaq === i ? '#a5b4fc' : 'rgba(255,255,255,0.4)',
                                            fontSize: '1.4rem', lineHeight: 1, fontWeight: 300,
                                            transition: 'color 0.25s'
                                        }}>
                                            {openFaq === i ? '−' : '+'}
                                        </span>
                                    </button>

                                    {openFaq === i && (
                                        <motion.div
                                            initial={{ opacity: 0, height: 0 }}
                                            animate={{ opacity: 1, height: 'auto' }}
                                            exit={{ opacity: 0, height: 0 }}
                                            transition={{ duration: 0.25 }}
                                            style={{
                                                background: 'rgba(99,102,241,0.07)',
                                                border: '1px solid rgba(99,102,241,0.4)',
                                                borderTop: 'none',
                                                borderRadius: '0 0 12px 12px',
                                                padding: '20px 24px 24px 72px'
                                            }}
                                        >
                                            <p style={{
                                                color: 'rgba(255,255,255,0.6)', fontSize: '0.95rem',
                                                lineHeight: 1.8, margin: 0
                                            }}>
                                                {faq.answer}
                                            </p>
                                        </motion.div>
                                    )}
                                </motion.div>
                            ))}
                        </div>

                        {/* Right: CTA card */}
                        <div className="col-lg-5">
                            <motion.div
                                initial={{ opacity: 0, x: 30 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.6, delay: 0.2 }}
                                viewport={{ once: true }}
                                style={{
                                    borderRadius: '20px', overflow: 'hidden', position: 'relative',
                                    background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 50%, #6366f1 100%)',
                                    padding: '52px 40px', textAlign: 'center',
                                    minHeight: '360px', display: 'flex', flexDirection: 'column',
                                    alignItems: 'center', justifyContent: 'center'
                                }}
                            >
                                {/* Decorative circles */}
                                <div style={{
                                    position: 'absolute', top: '-30px', right: '-30px',
                                    width: '140px', height: '140px', borderRadius: '50%',
                                    background: 'rgba(255,255,255,0.1)', pointerEvents: 'none'
                                }} />
                                <div style={{
                                    position: 'absolute', bottom: '30px', left: '-20px',
                                    width: '90px', height: '90px', borderRadius: '50%',
                                    background: 'rgba(255,255,255,0.07)', pointerEvents: 'none'
                                }} />
                                <div style={{
                                    position: 'absolute', top: '50px', left: '20px',
                                    width: '55px', height: '55px', borderRadius: '50%',
                                    background: 'rgba(255,255,255,0.08)', pointerEvents: 'none'
                                }} />
                                <div style={{
                                    position: 'absolute', bottom: '-20px', right: '40px',
                                    width: '70px', height: '70px', borderRadius: '50%',
                                    background: 'rgba(255,255,255,0.06)', pointerEvents: 'none'
                                }} />

                                {/* Chat icon */}
                                <div style={{
                                    width: '64px', height: '64px', borderRadius: '50%',
                                    background: 'rgba(255,255,255,0.15)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    marginBottom: '28px', position: 'relative', zIndex: 1
                                }}>
                                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke="white" strokeWidth="2" strokeLinejoin="round" />
                                    </svg>
                                </div>

                                <h3 style={{
                                    color: '#fff', fontWeight: 800,
                                    fontSize: 'clamp(1.4rem, 2.5vw, 1.75rem)',
                                    lineHeight: 1.3, marginBottom: '16px',
                                    position: 'relative', zIndex: 1
                                }}>
                                    Didn't Find What You Were Looking For?
                                </h3>
                                <p style={{
                                    color: 'rgba(255,255,255,0.7)', fontSize: '0.92rem',
                                    lineHeight: 1.7, marginBottom: '32px',
                                    position: 'relative', zIndex: 1, maxWidth: '280px'
                                }}>
                                    Our team of experts is ready to help you with any questions or custom requirements you may have.
                                </p>
                                <Link to="/contact" style={{ textDecoration: 'none', position: 'relative', zIndex: 1 }}>
                                    <button style={{
                                        display: 'inline-flex', alignItems: 'center', gap: '10px',
                                        background: '#fff', color: '#4f46e5',
                                        border: 'none', padding: '14px 28px',
                                        borderRadius: '50px', fontWeight: 700, fontSize: '0.92rem',
                                        cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s',
                                        boxShadow: '0 8px 24px rgba(0,0,0,0.2)'
                                    }}
                                        onMouseOver={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 14px 32px rgba(0,0,0,0.3)'; }}
                                        onMouseOut={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.2)'; }}
                                    >
                                        Get In Touch With Our Experts
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                            <path d="M5 12h14M12 5l7 7-7 7" stroke="#4f46e5" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </button>
                                </Link>
                            </motion.div>
                        </div>
                    </div>
                </div>
            </section>

            <ContactSection />
        </>
    );
};

export default EnterpriseNetworking;
